import { SELECTED_PRODUCTS_KEY } from "@constants/storageKeys"
import { INTERNAL_USER_DATA_PUSHED_FOR_ELEVAR } from "@hooks/useElevarEvents"
import { getIn } from "@utils/index"
import { getLocalStorage } from "@utils/storage"
import * as React from "react"

type Params = {
  cartTotal?: null | string
  checkoutDataFetched: boolean
  checkoutId?: null | string
  customer?: { email?: string; id?: string }
  customerDataFetched: boolean
  elevarOnUserData: (payload: any) => void
  pathname: string
  products: Product[]
}

type Product = {
  compareAtPrice?: string
  device?: string
  deviceType?: string
  image: string
  price: string
  productName: string
  quantity: number
  shopifyProductId: string
  shopifyVariantId: string
  sku: string
  variantName: string
}

export default function useElevarUserData({
  cartTotal,
  checkoutDataFetched,
  checkoutId,
  customer,
  customerDataFetched,
  elevarOnUserData,
  pathname,
  products,
}: Params) {
  const lastPushedPathRef = React.useRef<null | string>(null)

  React.useEffect(() => {
    const dataLayer = getIn(window, "dataLayer", []) as any[]
    const alreadyPushedForPath = dataLayer
      .slice()
      .reverse()
      .some(
        (e) =>
          e?.event === INTERNAL_USER_DATA_PUSHED_FOR_ELEVAR &&
          e?.location === pathname
      )

    if (alreadyPushedForPath) {
      lastPushedPathRef.current = pathname
      return
    }

    const selectedProducts = getLocalStorage(SELECTED_PRODUCTS_KEY) || {}
    const elevarProducts = (products || []).map((p, i) => {
      const list = selectedProducts[p.shopifyProductId] || "direct"
      return {
        brand: "Urban Armor Gear",
        compareAtPrice: p.compareAtPrice || "0.0",
        device: p.device,
        deviceType: p.deviceType,
        image: p.image,
        list,
        position: i + 1,
        price: p.price,
        productId: p.shopifyProductId,
        productName: p.productName,
        quantity: p.quantity,
        sku: p.sku,
        variantId: p.shopifyVariantId,
        variantName: p.variantName,
      }
    })

    const base = {
      cartTotal: cartTotal || "0.00",
      consent: true,
      event: INTERNAL_USER_DATA_PUSHED_FOR_ELEVAR,
      location: pathname,
      products: elevarProducts,
    }

    const hasCustomer = !!(customer?.id && customer?.email)
    const user = hasCustomer
      ? { email: customer!.email, id: customer!.id, loggedIn: true }
      : {}

    const shouldSend =
      // logged in + checkout present + both fetched
      (hasCustomer &&
        !!checkoutId &&
        customerDataFetched &&
        checkoutDataFetched) ||
      // logged in no checkout + customer fetched
      (hasCustomer && !checkoutId && customerDataFetched) ||
      // anon + checkout present + checkout fetched
      (!hasCustomer && !!checkoutId && checkoutDataFetched) ||
      // anon no checkout
      (!hasCustomer && !checkoutId)

    if (shouldSend) {
      elevarOnUserData({ ...base, ...user })
      lastPushedPathRef.current = pathname
    }
  }, [
    pathname,
    products,
    cartTotal,
    checkoutId,
    checkoutDataFetched,
    customerDataFetched,
    customer?.id,
    customer?.email,
    elevarOnUserData,
  ])
}
